import * as React from 'react';
import clsx from 'clsx';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { scaledImageLoader, SHOP_IMAGE_PLACEHOLDER } from 'utils/image';
import Image from 'next/image';
import { ScaledImage } from '@poinz/api';
import CarouselArrow from '../CarouselArrow';

interface Props {
  children: React.ReactNode;
  className?: string;
  pictures?: ScaledImage[];
  showOverlay?: boolean;
}

const ARROW_CLASS_NAME = 'absolute top-1/2 -translate-y-1/2';

const SETTINGS = Object.freeze({
  dots: false,
  speed: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  nextArrow: (
    // @ts-expect-error wrongly typed carousel pkg
    <CarouselArrow orientation="right" rootClass={clsx(ARROW_CLASS_NAME, '!right-[10px]')} />
  ),
  // @ts-expect-error wrongly typed carousel pkg
  prevArrow: <CarouselArrow orientation="left" rootClass={clsx(ARROW_CLASS_NAME, '!left-[10px]')} />
});

const DesktopCarousel: React.FC<Props> = ({
  children,
  className,
  pictures,
  showOverlay = true
}) => {
  const numberOfChildren = React.Children.count(children);
  const mainSliderRef = React.useRef<any>(null);
  const [indexInView, setIndexInView] = React.useState(0);
  const imagesRef = React.useRef<Record<string, React.MutableRefObject<HTMLImageElement>>>({});
  const imagesContainerRef = React.useRef<HTMLDivElement>(null);

  const scrollImageInView = React.useCallback((id: string) => {
    const container = imagesContainerRef.current;
    const selectedImage = imagesRef.current ? imagesRef.current[id] : null;

    if (selectedImage?.current && container) {
      const containerLeft = container.getBoundingClientRect().left;
      const imageLeft = selectedImage.current.getBoundingClientRect().left;

      container.scrollBy({
        left: imageLeft - containerLeft,
        behavior: 'smooth'
      });
    }
  }, []);

  React.useEffect(() => {
    const newRefs = pictures?.reduce((acc, _, index) => {
      acc[`image-${index}`] = acc[`image-${index}`] || React.createRef();
      return acc;
    }, {});

    if (newRefs) {
      imagesRef.current = newRefs;
    }
  }, [pictures]);

  React.useEffect(() => {
    scrollImageInView(`image-${indexInView}`);
  }, [indexInView, scrollImageInView]);

  const moreThenOneChildren = React.useMemo(() => numberOfChildren > 1, [numberOfChildren]);

  return (
    <div className={clsx('DesktopCarousel', className)}>
      <div className="slider-container">
        <Slider
          className="deal-slider mb-1"
          ref={slider => (mainSliderRef.current = slider)}
          {...SETTINGS}
          arrows={moreThenOneChildren}
          infinite={moreThenOneChildren}
          draggable={moreThenOneChildren}
          beforeChange={(_, nextSlideIndex) => {
            setIndexInView(nextSlideIndex);
          }}
        >
          {React.Children.map(children, (child, i) => (
            <div
              className={clsx('relative', moreThenOneChildren && 'cursor-grab')}
              key={`carousel-item-${i}`}
            >
              {showOverlay && <div className="bg-black/10 absolute inset-0 z-10 rounded-2xl"></div>}
              {child}
            </div>
          ))}
        </Slider>
        {pictures && moreThenOneChildren && (
          <div className="w-full flex gap-x-2 overflow-auto no-scrollbar" ref={imagesContainerRef}>
            {pictures.map((picture, index) => (
              <div
                key={`image-${index}`}
                className="relative rounded-lg overflow-hidden w-24 h-16 shrink-0 cursor-pointer"
                onClick={() => mainSliderRef.current?.slickGoTo(index)}
              >
                {indexInView === index && (
                  <div className="absolute inset-0 z-10 bg-black/40 rounded-lg"></div>
                )}
                <Image
                  src={picture?.sourceUrl || SHOP_IMAGE_PLACEHOLDER}
                  loader={scaledImageLoader()}
                  alt={`image-${index}`}
                  fill
                  className="object-cover"
                  ref={imagesRef.current[`image-${index}`] as any}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopCarousel;
